import PageBackTitle from "../Component/PageBackTitle";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Tabs, Tab, Snackbar } from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import Tooltip from "@mui/material/Tooltip";
import { HiOutlinePlus } from "react-icons/hi";
import { TbRefresh } from "react-icons/tb";
import { RxDownload } from "react-icons/rx";
import jsPDF from "jspdf";
import ReactDOM from "react-dom";
import html2canvas from "html2canvas";
import IsolationPermitTemplate from "../IsolationWorkPermit/IsolationPermitTemplate";
import {
  fetchIsolationPermit,
  listIsolationPermits,
} from "./isolationPermitApi";
import "../ColdWorkPermit/ColdWorkPermit.css";
import "../ColdWorkPermit/ColdWorkPermitForm.css";

const STATUS_TABS = [
  { value: "all", label: "All" },
  { value: "Isolation_Requested", label: "Isolation Requested" },
  { value: "Isolation_Certified", label: "Isolation Certified" },
  { value: "Energisation_Requested", label: "Energisation Requested" },
  { value: "Energisation_Certified", label: "Completed" },
];

const STATUS_COLORS = {
  Isolation_Requested: "#2563eb",
  Isolation_Certified: "#15803d",
  Energisation_Requested: "#b45309",
  Energisation_Certified: "#7c2d12",
};

const formatStatusLabel = (status) =>
  status ? status.replaceAll("_", " ") : "NA";

const formatDateTime = (value) => {
  if (!value) return "NA";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "NA";

  return `${date.toUTCString().split("GMT")[0].trim()} IST`;
};

const ISOLATION_WORK_PERMIT_NAME = "Electrical Isolation/Energisation";
const ENCODED_ISOLATION_WORK_PERMIT_NAME = encodeURIComponent(
  ISOLATION_WORK_PERMIT_NAME,
);
const PDF_PAGE_WIDTH = 210;
const PDF_PAGE_HEIGHT = 297;
const PDF_MARGIN = 5;
const ISOLATION_CAPTURE_WIDTH = 820;
const ISOLATION_PRINT_WIDTH = PDF_PAGE_WIDTH - PDF_MARGIN * 2;
const ISOLATION_PRINT_HEIGHT = PDF_PAGE_HEIGHT - PDF_MARGIN * 2;

function IsolationPermitList({ isSidebarExpanded }) {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState("all");
  const [permits, setPermits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const showSnackbar = useCallback((message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  }, []);

  const handleSnackbarClose = (_, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const loadPermits = useCallback(async () => {
    try {
      setLoading(true);
      const response = await listIsolationPermits();
      setPermits(response.permits || []);
    } catch (error) {
      showSnackbar(
        error?.response?.data?.message || "Failed to load isolation permits",
        "error",
      );
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  useEffect(() => {
    loadPermits();
  }, [loadPermits]);

  const tabCounts = useMemo(() => {
    const counts = {
      all: permits.length,
      Isolation_Requested: 0,
      Isolation_Certified: 0,
      Energisation_Requested: 0,
      Energisation_Certified: 0,
    };

    permits.forEach((permit) => {
      if (counts[permit.request_status] !== undefined) {
        counts[permit.request_status] += 1;
      }
    });

    return counts;
  }, [permits]);

  const filteredPermits = useMemo(() => {
    if (tabValue === "all") return permits;
    return permits.filter((permit) => permit.request_status === tabValue);
  }, [permits, tabValue]);

  const handleOpenNextStep = (permit) => {
    switch (permit.request_status) {
      case "Isolation_Requested":
        navigate(`/isolation-certificate/${permit.id}`);
        return;
      case "Isolation_Certified":
        navigate(`/request-energisation/${permit.id}`);
        return;
      case "Energisation_Requested":
        navigate(`/energisation-certificate/${permit.id}`);
        return;
      case "Energisation_Certified":
        navigate(
          `/view-pdf-formate/${permit.id}/${ENCODED_ISOLATION_WORK_PERMIT_NAME}/wp_requester`,
        );
        return;
      default:
        navigate(`/request-isolation/${permit.id}`);
    }
  };

  const getPrimaryActionLabel = (status) => {
    switch (status) {
      case "Isolation_Requested":
        return "Continue Iso Certificate";
      case "Isolation_Certified":
        return "Request Energisation";
      case "Energisation_Requested":
        return "Complete Energisation";
      case "Energisation_Certified":
        return "View Final Permit";
      default:
        return "Open";
    }
  };

  const handleDownloadPdf = async (permitId, serialNumber) => {
    let tempDiv;

    try {
      const payload = await fetchIsolationPermit(permitId);
      tempDiv = document.createElement("div");
      tempDiv.style.position = "fixed";
      tempDiv.style.left = "-10000px";
      tempDiv.style.top = "0";
      tempDiv.style.width = `${ISOLATION_CAPTURE_WIDTH}px`;
      tempDiv.style.padding = "0";
      tempDiv.style.margin = "0";
      tempDiv.style.background = "#ffffff";
      tempDiv.style.boxSizing = "border-box";
      document.body.appendChild(tempDiv);

      ReactDOM.render(
        <IsolationPermitTemplate
          permitDetails={payload}
          isSidebarExpanded={false}
        />,
        tempDiv,
      );

      const captureRoot =
        tempDiv.querySelector("#section1") ||
        tempDiv.querySelector("#isolation-section1") ||
        tempDiv.querySelector(".isolation-work-template");

      if (!captureRoot) {
        throw new Error("Isolation PDF template is not available");
      }

      const canvas = await html2canvas(captureRoot, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        windowWidth: ISOLATION_CAPTURE_WIDTH,
      });

      const pdf = new jsPDF("p", "mm", "a4");
      const imgData = canvas.toDataURL("image/jpeg", 0.95);
      pdf.addImage(
        imgData,
        "JPEG",
        PDF_MARGIN,
        PDF_MARGIN,
        ISOLATION_PRINT_WIDTH,
        ISOLATION_PRINT_HEIGHT,
      );

      const fileName = `${(serialNumber || "Isolation_Permit").replace(
        /[^\w-]+/g,
        "_",
      )}.pdf`;
      pdf.save(fileName);

    } catch (error) {
      showSnackbar(
        error?.message || "Failed to generate isolation permit PDF",
        "error",
      );
    } finally {
      if (tempDiv) {
        ReactDOM.unmountComponentAtNode(tempDiv);
        document.body.removeChild(tempDiv);
      }
    }
  };

  return (
    <>
      <div
        className={`data-list-content-container page-content ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="data-list-content">
          <div className="data-list-header">
            <div className="data-list-header-content">
              <PageBackTitle>Isolation Permits</PageBackTitle>
              <div className="data-list-heading-content-right-side">
                <Link to="/request-isolation" className="data-list-new">
                  <Tooltip title="New Isolation Permit" arrow>
                    <button className="new-button">
                      <HiOutlinePlus className="new-plus-icon" /> New Isolation
                    </button>
                  </Tooltip>
                </Link>
                <Tooltip title="Refresh List" arrow>
                  <button
                    className="refresh-button"
                    onClick={() => loadPermits()}
                  >
                    <TbRefresh className="refresh-icon" />
                  </button>
                </Tooltip>
              </div>
            </div>
          </div>
          <div className="header-separater"></div>
          <div className="permit-tab">
            <Tabs
              value={tabValue}
              onChange={(_, newValue) => setTabValue(newValue)}
              className="tabs-container"
            >
              {STATUS_TABS.map((tab) => (
                <Tab
                  key={tab.value}
                  value={tab.value}
                  label={
                    <div className="tab-label-container">
                      <span className="tab-status-label">{tab.label}</span>
                      <span className="tab-count">
                        ({tabCounts[tab.value] || 0})
                      </span>
                    </div>
                  }
                />
              ))}
            </Tabs>
            <div className="header-separater"></div>
            <div className="data-list-body">
              <div className="data-list-scroll-body-table">
                <div className="data-list-table-report">
                  {loading ? (
                    <div className="loader-container">
                      <div className="loader"></div>
                      <div>Loading isolation permits...</div>
                    </div>
                  ) : filteredPermits.length > 0 ? (
                    <table className="table multilevel-data">
                      <thead>
                        <tr>
                          <th className="center-alignment">SR. NO.</th>
                          <th className="center-alignment">REQUEST ID</th>
                          <th>PERMIT TYPE</th>
                          <th>EQUIPMENT</th>
                          <th>REQUEST STATUS</th>
                          <th>REQUESTED AT</th>
                          <th className="center-alignment">ACTION</th>
                        </tr>
                      </thead>
                      <tbody className="data-list-scroll-bodys">
                        {filteredPermits.map((permit, index) => (
                          <tr key={permit.id} className="hoverable-row">
                            <td className="center-alignment">{index + 1}</td>
                            <td
                              className="center-alignment"
                              style={{ color: "red" }}
                            >
                              {permit.request_id || "NA"}
                            </td>
                            <td>{permit.work_permit_name || "NA"}</td>
                            <td>
                              {permit.iso_equipment_number ||
                                permit.enrg_equipment_number ||
                                "NA"}
                            </td>
                            <td
                              style={{
                                color:
                                  STATUS_COLORS[permit.request_status] ||
                                  "#6B7280",
                                fontWeight: "bold",
                              }}
                            >
                              {formatStatusLabel(permit.request_status)}
                            </td>
                            <td>{formatDateTime(permit.created_at)}</td>
                            <td className="center-alignment">
                              <div className="permit-buttons1 center-alignment">
                                {permit.request_status ===
                                  "Isolation_Requested" && (
                                  <button
                                    className="new-button-download"
                                    onClick={() =>
                                      navigate(
                                        `/request-isolation/${permit.id}`,
                                      )
                                    }
                                  >
                                    Edit
                                  </button>
                                )}
                                {permit.request_status !==
                                  "Energisation_Certified" && (
                                  <button
                                    className="request-buttons permit-buttons"
                                    onClick={() => handleOpenNextStep(permit)}
                                    style={{ width: "12vw" }}
                                  >
                                    {getPrimaryActionLabel(
                                      permit.request_status,
                                    )}
                                  </button>
                                )}
                                {permit.request_status !==
                                  "Isolation_Requested" && (
                                  <button
                                    className="new-button-download"
                                    onClick={() =>
                                      navigate(
                                        `/view-pdf-formate/${permit.id}/${ENCODED_ISOLATION_WORK_PERMIT_NAME}/wp_requester`,
                                      )
                                    }
                                  >
                                    PDF
                                  </button>
                                )}
                                {permit.request_status !==
                                  "Isolation_Requested" && (
                                  <button
                                    className="new-button-download"
                                    onClick={() =>
                                      handleDownloadPdf(
                                        permit.id,
                                        permit.serial_number ||
                                          permit.request_id,
                                      )
                                    }
                                  >
                                    <RxDownload className="dnld-btn" />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="no-users-container">
                      <div className="no-users-message">
                        No isolation permits found.
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ marginTop: "35px", marginLeft: "20px" }}
      >
        <MuiAlert onClose={handleSnackbarClose} severity={snackbarSeverity}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </>
  );
}

export default IsolationPermitList;
